import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, Bell, Check, X as XIcon, Edit3 } from 'lucide-react';
import toast from 'react-hot-toast';

const timeAgo = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Agora mesmo';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `Há ${diffInMinutes}m`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `Há ${diffInHours}h`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) return 'Ontem';
  if (diffInDays < 30) return `Há ${diffInDays} dias`;
  
  return date.toLocaleDateString('pt-BR');
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const GlobalNotificationsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const API_URL = import.meta.env.VITE_API_URL || '';
  const token = localStorage.getItem('tt_auth_token');

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch notifications');
      const data = await res.json();
      setNotifications(data);

      const unreadCount = data.filter((n: any) => !n.read).length;
      if (unreadCount > 0) {
        await fetch(`${API_URL}/api/notifications/read-all`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const navigate = useNavigate();

  const handleApproveResult = async (matchId: string) => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/matches/${matchId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao aprovar');
      toast.success('Resultado aprovado!');
      fetchNotifications();
    } catch (err) {
      toast.error('Erro ao aprovar resultado');
    }
  };

  const handleRejectResult = async (matchId: string) => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/matches/${matchId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao rejeitar');
      toast.success('Resultado rejeitado!');
      fetchNotifications();
    } catch (err) {
      toast.error('Erro ao rejeitar resultado');
    }
  };

  const handleModifyResult = () => {
    onClose();
    navigate('/competitions');
  };

  const handleAcceptChallenge = async (matchId: string) => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/matches/${matchId}/accept-challenge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao aceitar');
      toast.success('Desafio aceito!');
      fetchNotifications();
    } catch (err) {
      toast.error('Erro ao aceitar desafio');
    }
  };

  const handleRejectChallenge = async (matchId: string) => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/matches/${matchId}/reject-challenge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao recusar');
      toast.success('Desafio recusado!');
      fetchNotifications();
    } catch (err) {
      toast.error('Erro ao recusar desafio');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(4px)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: "500px",
            backgroundColor: "#1A112C",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "8px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
          }}
        >
          <div
            style={{
              background: "var(--primary-color)",
              padding: "20px 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Bell size={24} color="#2D1E4B" />
              <h2 style={{ color: "#2D1E4B", fontSize: "20px", fontWeight: 700, margin: 0 }}>
                Notificações
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "none", color: "#2D1E4B", cursor: "pointer", display: "flex" }}
            >
              <X size={24} />
            </button>
          </div>

          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '60vh', overflowY: 'auto' }}>
            
            {loading ? (
              <p style={{ color: 'white', textAlign: 'center' }}>Carregando...</p>
            ) : notifications.length === 0 ? (
              <p style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>Nenhuma notificação encontrada.</p>
            ) : (
              notifications.map((notif: any) => {
                const metadata = notif.metadata ? JSON.parse(notif.metadata) : {};
                const isChallenge = notif.type === 'CHALLENGE';
                const isResultSubmitted = notif.type === 'RESULT_SUBMITTED';

                return (
                  <div key={notif.id} style={{ background: notif.read ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)', padding: '16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <h4 style={{ color: 'white', margin: '0 0 4px 0', fontSize: '15px' }}>{notif.title}</h4>
                        <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '13px' }}>
                          {timeAgo(notif.createdAt)}
                        </p>
                      </div>
                      {!notif.read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--primary-color)' }} />}
                    </div>
                    
                    <p style={{ color: 'rgba(255,255,255,0.8)', margin: (isChallenge || isResultSubmitted) ? '0 0 16px 0' : 0, fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: notif.message }}></p>
                    
                    {isResultSubmitted && metadata.score && (
                      <div style={{ background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>
                        <p style={{ margin: 0, color: 'white', fontWeight: 600, textAlign: 'center' }}>Placar Reportado:</p>
                        <p style={{ margin: '4px 0 0 0', color: '#A8FF00', fontSize: '18px', fontWeight: 'bold', textAlign: 'center' }}>{metadata.score}</p>
                      </div>
                    )}
                    
                    {isChallenge && metadata.matchId && metadata.matchStatus === 'CHALLENGE_PROPOSED' && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => handleRejectChallenge(metadata.matchId)} style={{ flex: 1, padding: '8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }}>
                          <XIcon size={16} /> Recusar
                        </button>
                        <button onClick={() => handleAcceptChallenge(metadata.matchId)} style={{ flex: 1, padding: '8px', background: 'var(--primary-color)', border: 'none', borderRadius: '6px', color: '#2D1E4B', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }}>
                          <Check size={16} /> Aceitar
                        </button>
                      </div>
                    )}

                    {isResultSubmitted && metadata.matchId && metadata.matchStatus === 'AWAITING_APPROVAL' && (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button onClick={() => handleRejectResult(metadata.matchId)} style={{ flex: 1, minWidth: '80px', padding: '8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }}>
                          <XIcon size={16} /> Rejeitar
                        </button>
                        <button onClick={handleModifyResult} style={{ flex: 1, minWidth: '80px', padding: '8px', background: 'transparent', border: '1px solid var(--primary-color)', borderRadius: '6px', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }}>
                          <Edit3 size={16} /> Modificar
                        </button>
                        <button onClick={() => handleApproveResult(metadata.matchId)} style={{ flex: 1, minWidth: '80px', padding: '8px', background: 'var(--primary-color)', border: 'none', borderRadius: '6px', color: '#2D1E4B', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }}>
                          <Check size={16} /> Aceitar
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}

          </div>
          
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
